import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Input, message } from "antd";
import styled from "styled-components";
import { CreatorDisplayLayout } from "../common/CreatorDisplayLayout";
import { CreatorGroupGrid } from "./CreatorGroupGrid";
import { DynamicModal } from "@/shared/components/Modal/DynamicModal";
import { creatorListAPI } from "@/api/creatorList";
import { blockAPI, BlockData, BlockType } from "@/api/block";
import { engagementTermsAPI } from "@/api/engagementTerms";
import { createShortUrl, buildShortUrl } from "@/api/shortUrl";
import { hasRole, ROLES } from "@/api/user";
import { creatorAPI } from "@/api/creator";
import { applyCreatorGroupSummary } from "@/shared/utils/creatorGroupSummary";
import { extractAvatarUrl } from "@/shared/utils/socialProfile";
import type { Creator, CreatorGroup } from "@/shared/types/campaign/creator";
import { ResponsiveAvatar } from "@/shared/components/Avatar/ResponsiveAvatar";
import { campaignAPI, isCetEnabled } from "@/api/campaign";
import { Icon } from "@/shared/components/Icon";
import { LoadingWrapper } from "@/shared/components";
import { mediaPhone } from "@/shared/theme/breakpoints";
import { normalizeEmail } from "@/shared/utils/email";

const EmptyGroups = styled.div`
	padding: 40px;
	text-align: center;
	color: #999;

	${mediaPhone`
		padding: 32px 0;
	`}
`;

const PageContainer = styled.div`
	padding: 24px;
	background: #eaeefd;
	min-height: 300px;
	border-radius: 16px;
`;

interface CreatorGroupingProps {
	userAssignment?: {
		isAssigned: boolean;
		roles: string[];
	} | null;
	onCreatorCampaignStatusChanged?: () => void; // Invoked when creator is added to or removed from campaign
}

export const CreatorGrouping: React.FC<CreatorGroupingProps> = ({ userAssignment, onCreatorCampaignStatusChanged }) => {
	const [searchParams] = useSearchParams();
	const campaignId = searchParams.get("campaignId");
	const blockId = searchParams.get("blockId");
	const navigate = useNavigate();
	const [groups, setGroups] = useState<CreatorGroup[]>([]);
	const [clientVisible, setClientVisible] = useState(false);
	const [creatorVisibility, setCreatorVisibility] = useState<{ [key: string]: boolean }>({});
	const [isCustomizing, setIsCustomizing] = useState(false);
	const [orderedGroups, setOrderedGroups] = useState<CreatorGroup[]>([]);
	const [savedVisibility, setSavedVisibility] = useState<{ [key: string]: boolean }>({});
	const [savedClientVisible, setSavedClientVisible] = useState(false);
	const [loading, setLoading] = useState(true);
	const [contentExecutionBlockId, setContentExecutionBlockId] = useState<string>("");
	const [marketBlockId, setMarketBlockId] = useState<string | null>(null);
	const [marketCountry, setMarketCountry] = useState<string | null>(null);
	const [engagementTermsRequiredModalVisible, setEngagementTermsRequiredModalVisible] = useState(false);
	// Tracks in-flight per-creator Engagement Terms checks. A ref (not state) so
	// the guard below is synchronous — a state-backed guard can be bypassed by a
	// double-click before React commits the first click's state update.
	const checkingEngagementTermsForRef = useRef<Set<string>>(new Set());
	const [finalRateCurrency, setFinalRateCurrency] = useState<string>("USD");
	const [finalRateConvertedCurrency, setFinalRateConvertedCurrency] = useState<string>("AUD");
	const [campaignName, setCampaignName] = useState("Campaign");
	const [addCreatorModalVisible, setAddCreatorModalVisible] = useState(false);
	const [selectedCreator, setSelectedCreator] = useState<any>(null);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [creatorToDelete, setCreatorToDelete] = useState<{
		groupId: string;
		creatorId: string;
		assignmentId: string;
		creatorName: string;
	} | null>(null);
	const [isDeletingCreator, setIsDeletingCreator] = useState(false);
	const [creatorToRemoveFromCampaign, setCreatorToRemoveFromCampaign] = useState<{
		assignmentId: string;
		creatorName: string;
		groupId?: string;
	} | null>(null);
	const [isRemovingFromCampaign, setIsRemovingFromCampaign] = useState(false);
	const [isSaving, setIsSaving] = useState(false);

	useEffect(() => {
		if (!campaignId) return;
		campaignAPI
			.getById(campaignId)
			.then((campaign) => setCampaignName(campaign.name || "Campaign"))
			.catch((error) => console.error("Failed to load campaign for Creator Grouping", error));
	}, [campaignId]);

	const handleCategoryTierChange = (creatorId: string, field: "categories" | "tier", value: string[] | string) => {
		if (!campaignId) return;

		// Optimistic: update local state immediately
		setGroups((prev) =>
			prev.map((group) => ({
				...group,
				creators: group.creators.map((creator) => {
					if (creator.id !== creatorId) return creator;
					return {
						...creator,
						...(field === "categories" && { categories: value, category: value }),
						...(field === "tier" && { tier: value, creatorTier: value }),
					};
				}),
			})),
		);

		// Fire API in background, revert on failure
		const apiCall =
			field === "categories"
				? creatorAPI.updateCreatorCategories(creatorId, value as string[], campaignId)
				: creatorAPI.updateCreatorTier(creatorId, value as string, campaignId);

		apiCall.catch(() => {
			message.error(`Failed to update ${field === "categories" ? "category" : "tier"}`);
		});
	};

	// Check if user is client only
	const STAFF_ROLES = ["Lead", "Support", "Account Manager"];
	const isClientOnly =
		(userAssignment?.roles ?? []).some((r) => r.toLowerCase() === "client") &&
		!(userAssignment?.roles ?? []).some((r) => STAFF_ROLES.includes(r));
	const isAgencyInCampaign = (userAssignment?.roles ?? []).some((r) => STAFF_ROLES.includes(r));
	const canManage = hasRole(ROLES.ADMIN) || isAgencyInCampaign;

	// Helper function to find the market block (and its Content Execution child)
	// that owns the current (Creator Grouping) block.
	const findMarketAndContentExecution = useCallback(
		(
			currentBlockId: string,
			blocks: BlockData[],
		): { market: BlockData | null; contentExecution: BlockData | null } => {
			// Find all blocks that have currentBlockId in their target array
			const findParents = (blockId: string, visited = new Set<string>()): BlockData[] => {
				if (visited.has(blockId)) return [];
				visited.add(blockId);

				const parents = blocks.filter((block) => block.target.includes(blockId));
				const allParents = [...parents];

				parents.forEach((parent) => {
					allParents.push(...findParents(parent.id, visited));
				});

				return allParents;
			};

			// Find all descendants of a block
			const findDescendants = (blockId: string, visited = new Set<string>()): BlockData[] => {
				if (visited.has(blockId)) return [];
				visited.add(blockId);

				const block = blocks.find((b) => b.id === blockId);
				if (!block) return [];

				const descendants: BlockData[] = [block];
				block.target.forEach((targetId) => {
					descendants.push(...findDescendants(targetId, visited));
				});

				return descendants;
			};

			// Get all ancestors (parents recursively)
			const ancestors = findParents(currentBlockId);

			// Find market blocks among ancestors
			const marketBlocks = ancestors.filter((block) => block.type === BlockType.MARKET);

			// For each market, find Content Execution in its descendants
			for (const market of marketBlocks) {
				const descendants = findDescendants(market.id);
				const contentExecution = descendants.find((block) => block.type === BlockType.CONTENT_EXECUTION);
				if (contentExecution) {
					return { market, contentExecution };
				}
			}

			return { market: marketBlocks[0] ?? null, contentExecution: null };
		},
		[],
	);

	// Load content execution block ID + the owning market block ID (used to
	// check whether the market has Engagement Terms before allowing a creator
	// to be added to Content Execution).
	useEffect(() => {
		const loadContentExecutionBlock = async () => {
			if (!campaignId || !blockId) return;

			try {
				const blocks = await blockAPI.getByCampaignId(campaignId);
				const { market, contentExecution } = findMarketAndContentExecution(blockId, blocks);

				// Keep these in sync — a stale contentExecutionBlockId from a
				// previous grouping paired with a freshly-updated marketBlockId
				// could otherwise let a creator get added to the wrong market's
				// Content Execution block.
				if (contentExecution) {
					setContentExecutionBlockId(contentExecution.id);
				} else {
					console.warn("No Content Execution block found for this grouping");
					setContentExecutionBlockId("");
				}
				setMarketBlockId(market?.id ?? null);
				setMarketCountry(market?.name ?? null);
			} catch (error) {
				console.error("Error loading content execution block:", error);
			}
		};

		loadContentExecutionBlock();
	}, [campaignId, blockId, findMarketAndContentExecution]);

	const loadCreatorGroups = useCallback(async () => {
		if (!campaignId || !blockId) {
			console.warn("Missing campaignId or blockId");
			setLoading(false);
			return;
		}

		try {
			setLoading(true);

			// Fetch group display settings to get currency information
			try {
				const campaignRate = await creatorListAPI.getCampaignRate(campaignId);
				if (campaignRate) {
					setFinalRateCurrency(campaignRate.finalCurrency || "USD");
					setFinalRateConvertedCurrency(campaignRate.finalConvertedCurrency || "AUD");
				}
			} catch (error) {
				console.warn("Failed to fetch campaign rate, using defaults:", error);
				// Use defaults if fetch fails
				setFinalRateCurrency("USD");
				setFinalRateConvertedCurrency("AUD");
			}

			const groupsData = await creatorListAPI.getCreatorGroups(campaignId, blockId);

			// Ensure we always have an array and sort by index (NULL values last), then by name as fallback
			const groups = (Array.isArray(groupsData) ? groupsData : []).sort((a, b) => {
				// Sort by index (NULL values go last)
				if (a.index === null || a.index === undefined) return 1;
				if (b.index === null || b.index === undefined) return -1;
				if (a.index !== b.index) return a.index - b.index;
				// If indices are equal, sort by name
				return (a.name || "").localeCompare(b.name || "");
			});

			// Fetch creators for each group
			const groupsWithCreators = await Promise.all(
				groups.map(async (group) => {
					try {
						const creatorsData = await creatorListAPI.getCreatorsFromGroup(campaignId, blockId, group.id);

						// Transform CreatorWithAssignmentResponse to Creator format
						const creators = creatorsData.map((item) => {
							const isClientVisible = item.assignment?.clientVisible === true;
							const creator: any = {
								id: item.creator.id,
								assignmentId: item.assignment.id, // Add assignment ID
								name: item.creator.name || "Unknown",
								img_url: extractAvatarUrl(item.socialAccounts),
								categories: item.creator.categories || [],
								tier: item.creator.tier || "",
								sampleContents: item.creator.sampleContents || [],
								rationaleForProposing: item.assignment?.recommendation?.length
									? item.assignment.recommendation.join(", ")
									: item.creator.rationaleForProposing || "",
								location: item.creator.location || "",
								userId: item.creator.userId || null,
								contactEmail: item.creator.contactEmail || "",
								addedToCampaign: item.assignment?.addedToCampaign || false,
								creatorInterested: item.assignment?.creatorInterested,
								finalRate: isClientVisible ? (item.assignment?.finalRate ?? null) : null,
								finalConvertedRate: isClientVisible
									? (item.assignment?.finalConvertedRate ?? null)
									: null,
								socialAccounts: item.socialAccounts || {},
								assignment: item.assignment,
								recommendation: item.assignment?.recommendation || [],
							};

							// Add social account data per platform
							Object.entries(item.socialAccounts || {}).forEach(([platform, account]) => {
								const normalizedPlatform = platform.toLowerCase();
								creator[normalizedPlatform] = {
									followers: account.followers,
									avgEngagementRate: account.avgEngagementRate,
									avgViews: account.avgViews,
									localAudiencePercentage: account.localAudiencePercentage,
									femalePercentage: account.femalePercentage,
									malePercentage: account.malePercentage,
									audienceAgeDistribution: account.audienceAgeDistribution,
									platformUsername: account.platformUsername,
									avatarUrl: account.avatarUrl,
								};
							});

							// Add custom info fields
							Object.entries(item.customInfo || {}).forEach(([key, value]) => {
								creator[key] = value;
							});

							return creator;
						});

						return applyCreatorGroupSummary(
							{
								...group,
								creators,
							},
							isClientOnly,
							creatorVisibility,
						);
					} catch (error) {
						console.error(`Error loading creators for group ${group.id}:`, error);
						return applyCreatorGroupSummary(
							{
								...group,
								creators: [], // Return empty array on error
							},
							isClientOnly,
							creatorVisibility,
						);
					}
				}),
			);

			setGroups(groupsWithCreators);
			setOrderedGroups(groupsWithCreators);
		} catch (error) {
			console.error("Error loading creator groups:", error);
			message.error((error as Error).message || "Failed to load creator groups");
			setGroups([]);
			setOrderedGroups([]);
		} finally {
			setLoading(false);
		}
	}, [campaignId, blockId, isClientOnly]);

	useEffect(() => {
		if (campaignId && blockId) {
			loadCreatorGroups();
		}
	}, [campaignId, blockId, loadCreatorGroups]);

	const handleAddCreator = () => {
		// Navigate to Create New Group page with campaign, block, and display info
		const displayIds = groups.map((group) => group.id).join(",");
		navigate(
			`/campaign-detail/create-new-group?campaignId=${campaignId}&blockId=${blockId}&displayId=${displayIds}`,
		);
	};

	const handleGetUrl = async () => {
		const path = window.location.pathname + window.location.search;
		try {
			const shortUrl = await createShortUrl(path);
			await navigator.clipboard.writeText(buildShortUrl(shortUrl.code, path));
			message.success("Link copied to clipboard!");
		} catch {
			await navigator.clipboard.writeText(path);
			message.success("Link copied to clipboard!");
		}
	};

	const handleCustomizeOrder = () => {
		if (isCustomizing) {
			setIsCustomizing(false);
			setCreatorVisibility(savedVisibility);
			setClientVisible(savedClientVisible);
			setOrderedGroups([...groups]);
			message.info("Customization cancelled");
		} else {
			// Start customization
			setOrderedGroups([...groups]);
			setSavedVisibility({ ...creatorVisibility });
			setSavedClientVisible(clientVisible);
			setIsCustomizing(true);
			message.info("Drag groups to reorder");
		}
	};

	const handleSaveOrder = async () => {
		if (!campaignId || !blockId) {
			message.error("Missing campaign or block information");
			return;
		}

		setIsSaving(true);
		try {
			const groupIndices = orderedGroups.map((group, index) => ({
				groupId: group.id,
				index: index,
			}));

			await creatorListAPI.updateCreatorGroupIndices(campaignId, blockId, groupIndices);

			// Prepare creator visibility updates (only send changed values)
			const visibilityUpdates: { assignmentId: string; clientVisible: boolean }[] = [];
			orderedGroups.forEach((group) => {
				(group.creators || []).forEach((creator) => {
					const prev = savedVisibility[creator.id];
					const curr = creatorVisibility[creator.id];
					// If visibility changed (including undefined -> explicit), and we have an assignmentId, queue update
					if (creator.assignmentId && curr !== prev) {
						visibilityUpdates.push({ assignmentId: creator.assignmentId, clientVisible: !!curr });
					}
				});
			});

			if (visibilityUpdates.length > 0) {
				try {
					await creatorListAPI.updateClientVisibleToCreatorAssignments(
						campaignId,
						blockId,
						visibilityUpdates,
					);
				} catch (err) {
					console.error("Error saving creator visibility changes:", err);
					message.error("Failed to save creator visibility changes");
				}
			}

			setGroups(orderedGroups);
			setSavedVisibility({ ...creatorVisibility });
			setSavedClientVisible(clientVisible);
			setIsCustomizing(false);
			message.success("Changes saved successfully!");
		} catch (error) {
			console.error("Error saving group order:", error);
			message.error("Failed to save group order");
		} finally {
			setIsSaving(false);
		}
	};

	const handleClientVisibleToggle = () => {
		if (!isCustomizing) {
			setOrderedGroups([...groups]);
			setSavedVisibility({ ...creatorVisibility });
			setSavedClientVisible(clientVisible);
			setIsCustomizing(true);
		}

		const newClientVisible = !clientVisible;
		setClientVisible(newClientVisible);

		// When turning on "Visible to Client", set all creator toggles to true
		if (newClientVisible) {
			const allVisible: { [key: string]: boolean } = {};
			groups.forEach((group) => {
				group.creators.forEach((creator) => {
					allVisible[creator.id] = true;
				});
			});
			setCreatorVisibility(allVisible);
		} else {
			// When turning off "Visible to Client", set all creator toggles to false
			const allInvisible: { [key: string]: boolean } = {};
			groups.forEach((group) => {
				group.creators.forEach((creator) => {
					allInvisible[creator.id] = false;
				});
			});
			setCreatorVisibility(allInvisible);
		}
	};

	const handleCreatorVisibilityToggle = (creatorId: string) => {
		if (!isCustomizing) {
			setOrderedGroups([...groups]);
			setSavedVisibility({ ...creatorVisibility });
			setSavedClientVisible(clientVisible);
			setIsCustomizing(true);
		}

		const newVisibility = {
			...creatorVisibility,
			[creatorId]: !creatorVisibility[creatorId],
		};
		setCreatorVisibility(newVisibility);

		// Check if all creators are now invisible
		const allInvisible = groups.every((group) => group.creators.every((creator) => !newVisibility[creator.id]));

		// If all creators are invisible, turn off the main toggle
		if (allInvisible && clientVisible) {
			setClientVisible(false);
		}
	};

	const handleSettingsClick = () => {
		message.info("Creator settings functionality coming soon");
	};

	const handleCommentClick = () => {
		message.info("Comments functionality coming soon");
	};

	const handleCustomizeDataInGroups = () => {
		// Use displayType=group so CustomizeColumns knows to load settings by blockId for all groups
		navigate(
			`/campaign-detail/customize-columns?campaignId=${campaignId}&blockId=${blockId}&displayType=group&pageTitle=Creator Grouping`,
		);
	};

	const handleDeleteCreator = (groupId: string, creatorId: string) => {
		if (!campaignId || !blockId) {
			message.error("Missing campaign or block information");
			return;
		}

		// Find the creator to get the assignmentId and name
		const group = groups.find((g) => g.id === groupId);
		const creator = group?.creators.find((c) => c.id === creatorId);

		if (!creator || !creator.assignmentId) {
			message.error("Creator not found or missing assignment ID");
			return;
		}

		const creatorName = creator.name || "Creator";

		setCreatorToDelete({
			groupId,
			creatorId,
			assignmentId: creator.assignmentId,
			creatorName,
		});
	};

	const handleConfirmDeleteCreator = async () => {
		if (!creatorToDelete || !campaignId || !blockId) return;

		setIsDeletingCreator(true);
		try {
			await creatorListAPI.removeCreatorFromGroup(
				campaignId,
				blockId,
				creatorToDelete.groupId,
				creatorToDelete.assignmentId,
			);

			message.success("Creator removed from group successfully!");

			const updateGroupsState = (prevGroups: CreatorGroup[]) =>
				prevGroups.map((group) => {
					if (group.id !== creatorToDelete.groupId) {
						return group;
					}

					const updatedCreators = group.creators.filter((c) => c.id !== creatorToDelete.creatorId);

					let sumPrimary = 0;
					let sumConverted = 0;
					updatedCreators.forEach((c) => {
						if (c.finalRate !== null && c.finalRate !== undefined) {
							sumPrimary += c.finalRate;
						}
						if (c.finalConvertedRate !== null && c.finalConvertedRate !== undefined) {
							sumConverted += c.finalConvertedRate;
						}
					});

					return {
						...group,
						creators: updatedCreators,
						sumOfFinalRate: {
							primary: sumPrimary > 0 ? sumPrimary.toFixed(2) : undefined,
							converted: sumConverted > 0 ? sumConverted.toFixed(2) : undefined,
						},
						totalCreators: updatedCreators.length,
					};
				});

			setGroups(updateGroupsState);
			setOrderedGroups(updateGroupsState);
			setCreatorToDelete(null);
		} catch (error) {
			console.error("Error removing creator from group:", error);
			message.error("Failed to remove creator from group");
		} finally {
			setIsDeletingCreator(false);
		}
	};

	const handleAddToCampaign = async (assignmentId: string) => {
		if (checkingEngagementTermsForRef.current.has(assignmentId)) return;
		if (!campaignId || !blockId) {
			message.error("Missing campaign or block information");
			return;
		}

		if (!contentExecutionBlockId) {
			message.error("Content Execution block not found for this market");
			return;
		}

		// Find the creator from groups to get their data
		let creator = null;
		for (const group of groups) {
			const foundCreator = group.creators?.find((c) => c.assignmentId === assignmentId);
			if (foundCreator) {
				creator = foundCreator;
				break;
			}
		}

		if (!creator) {
			message.error("Creator not found");
			return;
		}

		// Content Execution is gated behind an existing Engagement Term for this
		// creator's market (Ticket #354) — prompt to create one instead of the
		// normal Add Creator flow when the market doesn't have one yet.
		// Campaigns without CET skip the gate. The setting is read fresh on every click because it can be
		// switched in Edit Campaign Details while this view stays open.
		if (marketBlockId) {
			checkingEngagementTermsForRef.current.add(assignmentId);
			try {
				let campaignCetEnabled = true;
				try {
					campaignCetEnabled = isCetEnabled(await campaignAPI.getById(campaignId));
				} catch (error) {
					// Unknown: keep the check; the server-side gate decides either way.
					console.error("Failed to load campaign CET setting", error);
				}
				if (!campaignCetEnabled) {
					setSelectedCreator(creator);
					setAddCreatorModalVisible(true);
					return;
				}
				const marketTerms = await engagementTermsAPI.getMarketTerms(campaignId, marketBlockId);
				if (!marketTerms) {
					setSelectedCreator(creator);
					setEngagementTermsRequiredModalVisible(true);
					return;
				}
			} catch (error) {
				console.error("Error checking engagement terms before adding creator:", error);
				// Fail closed: don't fall through to the normal Add Creator modal
				// on a check failure — the server-side gate would just bounce the
				// submission anyway, after the user has already filled in a form.
				message.error("Couldn't verify Engagement Terms for this market. Please try again.");
				return;
			} finally {
				checkingEngagementTermsForRef.current.delete(assignmentId);
			}
		}

		// Set creator data and show modal
		setSelectedCreator(creator);
		setAddCreatorModalVisible(true);
	};

	const updateCreatorCampaignStatus = (assignmentId: string, addedToCampaign: boolean) => {
		const updateGroupsState = (prevGroups: CreatorGroup[]) =>
			prevGroups.map((group) => ({
				...group,
				creators: group.creators.map((creator) =>
					creator.assignmentId === assignmentId ? { ...creator, addedToCampaign } : creator,
				),
			}));

		setGroups(updateGroupsState);
		setOrderedGroups(updateGroupsState);
	};

	const updateCreatorContactEmail = (assignmentId: string, contactEmail: string | null) => {
		const updateGroupsState = (prevGroups: CreatorGroup[]) =>
			prevGroups.map((group) => ({
				...group,
				creators: group.creators.map((creator) =>
					creator.assignmentId === assignmentId
						? {
								...creator,
								contactEmail: contactEmail || "",
								assignment: { ...creator.assignment, contactEmail },
							}
						: creator,
				),
			}));
		setGroups(updateGroupsState);
		setOrderedGroups(updateGroupsState);
	};

	const handleSubmitAddToCampaign = async (email: string | null) => {
		if (isSubmitting) return;

		if (!campaignId || !blockId || !selectedCreator) {
			message.error("Missing campaign or block information");
			return;
		}

		if (!contentExecutionBlockId) {
			message.error("Content Execution block not found for this market");
			return;
		}

		setIsSubmitting(true);
		try {
			const creatorName = selectedCreator.name?.trim() || getPlatformUsername(selectedCreator);

			// Pass contentExecutionBlockId and creatorName to the API
			await creatorListAPI.addCreatorToCampaign(
				campaignId,
				blockId,
				selectedCreator.assignmentId,
				contentExecutionBlockId,
				creatorName,
				email,
			);
			message.success("Creator added to campaign successfully!");

			// Close modal and reset form
			updateCreatorCampaignStatus(selectedCreator.assignmentId, true);
			setAddCreatorModalVisible(false);
			setSelectedCreator(null);
			onCreatorCampaignStatusChanged && onCreatorCampaignStatusChanged();
		} catch (error) {
			console.error("Error adding creator to campaign:", error);
			if (selectedCreator?.assignmentId) {
				updateCreatorCampaignStatus(selectedCreator.assignmentId, false);
			}
			// Server-side gate (defense in depth against the client-side pre-check
			// going stale, e.g. terms removed between check and submit).
			if (axios.isAxiosError(error) && error.response?.data?.error === "ENGAGEMENT_TERMS_REQUIRED") {
				setAddCreatorModalVisible(false);
				setEngagementTermsRequiredModalVisible(true);
				return;
			}
			message.error("Failed to add creator to campaign");
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleCancelAddToCampaign = () => {
		if (isSubmitting) return;
		setAddCreatorModalVisible(false);
		setSelectedCreator(null);
	};

	const handleCancelEngagementTermsRequired = () => {
		setEngagementTermsRequiredModalVisible(false);
		setSelectedCreator(null);
	};

	// Blue button on the "Engagement Term required" prompt: open Edit Campaign
	// Detail directly on the Engagement Terms tab so the user can fill in the
	// form for this market before retrying Add to Campaign.
	const handleGoToEngagementTerms = () => {
		if (!campaignId) return;
		setEngagementTermsRequiredModalVisible(false);
		setSelectedCreator(null);
		const params = new URLSearchParams({ campaignId, manage: "1", tab: "engagementTerms" });
		if (blockId) params.set("blockId", blockId);
		// Pre-select this creator's market on a multi-market campaign — otherwise
		// the Engagement Terms tab defaults to the first market, which may not be
		// the one that was actually gated.
		if (marketCountry) params.set("market", marketCountry);
		navigate(`/campaign-detail?${params.toString()}`);
	};

	const handleRemoveFromCampaign = (assignmentId: string, groupId?: string) => {
		if (!campaignId || !blockId) {
			message.error("Missing campaign or block information");
			return;
		}

		// Find the creator from groups to get their name
		let creatorName = "Creator";
		for (const group of groups) {
			const creator = group.creators?.find((c) => c.assignmentId === assignmentId);
			if (creator) {
				creatorName = creator.name || "Creator";
				break;
			}
		}

		setCreatorToRemoveFromCampaign({ assignmentId, creatorName, groupId });
	};

	const handleConfirmRemoveFromCampaign = async () => {
		if (!creatorToRemoveFromCampaign || !campaignId || !blockId) return;

		setIsRemovingFromCampaign(true);
		try {
			await creatorListAPI.removeCreatorFromCampaign(
				campaignId,
				blockId,
				creatorToRemoveFromCampaign.assignmentId,
				creatorToRemoveFromCampaign.groupId,
			);
			updateCreatorCampaignStatus(creatorToRemoveFromCampaign.assignmentId, false);
			message.success("Creator removed from campaign successfully!");
			onCreatorCampaignStatusChanged && onCreatorCampaignStatusChanged();
			setCreatorToRemoveFromCampaign(null);
		} catch (error) {
			console.error("Error removing creator from campaign:", error);
			updateCreatorCampaignStatus(creatorToRemoveFromCampaign.assignmentId, true);
			message.error("Failed to remove creator from campaign");
		} finally {
			setIsRemovingFromCampaign(false);
		}
	};

	const handleGroupReorder = (reorderedGroups: CreatorGroup[]) => {
		setOrderedGroups(reorderedGroups);
	};

	const handleSeeApprovals = (groupId: string) => {
		// TODO: Implement see approvals functionality
		console.log("See approvals for group:", groupId);
	};

	const handleGroupVisibilityToggle = (groupId: string) => {
		// TODO: Implement group visibility toggle
		console.log("Toggle visibility for group:", groupId);
	};

	const handleEditGroup = (groupId: string) => {
		// Open the same Create / Edit Group configuration window as the "Creator New Group" button
		if (!campaignId || !blockId) {
			message.error("Missing campaign or block information");
			return;
		}

		// Use the same displayIds pattern as handleAddCreator so the target page
		// can see all existing groups under this block, and also pass the
		// specific groupId being edited.
		const displayIds = groups.map((group) => group.id).join(",");
		navigate(
			`/campaign-detail/create-new-group?campaignId=${campaignId}&blockId=${blockId}&displayId=${displayIds}&groupId=${groupId}`,
		);
	};

	const handleDeleteGroup = async (groupId: string) => {
		if (!campaignId || !blockId) {
			message.error("Missing campaign or block information");
			return;
		}

		const group = groups.find((g) => g.id === groupId);
		if (!group) {
			message.error("Group not found");
			return;
		}

		try {
			// Ask backend to delete the group resource. Backend will clean up assignments/approvals/display settings.
			await creatorListAPI.deleteCreatorGroup(campaignId, blockId, groupId);

			// Remove group locally from state
			const updateGroupsState = (prevGroups: CreatorGroup[]) => prevGroups.filter((g) => g.id !== groupId);
			setGroups(updateGroupsState);
			setOrderedGroups(updateGroupsState);
			message.success("Group deleted successfully");
		} catch (error) {
			console.error("Error deleting group:", error);
			message.error("Failed to delete group");
		}
	};

	// Filter creators for client-only view
	const displayGroups = isCustomizing ? orderedGroups : groups;
	const displayGroupsWithSummary = useMemo(
		() => displayGroups.map((group) => applyCreatorGroupSummary(group, isClientOnly, creatorVisibility)),
		[displayGroups, isClientOnly, creatorVisibility],
	);

	// Initialize orderedGroups and visibility when groups change
	useEffect(() => {
		if (groups.length > 0) {
			setOrderedGroups([...groups]);

			// Initialize visibility for all creators from assignment.clientVisible if not already set
			const initialVisibility: { [key: string]: boolean } = {};
			groups.forEach((group) => {
				(group.creators || []).forEach((creator) => {
					if (creatorVisibility[creator.id] === undefined) {
						initialVisibility[creator.id] =
							creator.assignment?.clientVisible !== undefined
								? !!creator.assignment.clientVisible
								: false;
					}
				});
			});

			if (Object.keys(initialVisibility).length > 0) {
				setCreatorVisibility((prev) => ({ ...prev, ...initialVisibility }));
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [groups]);

	if (loading) {
		return (
			<LoadingWrapper isLoading label="Loading creator groups" borderRadius={12}>
				<PageContainer />
			</LoadingWrapper>
		);
	}

	// Show empty state if no groups
	if (!loading && groups.length === 0) {
		return (
			<CreatorDisplayLayout
				pageTitle="Creator Grouping"
				addCreatorText="Create New Group"
				displayId=""
				mode="grouping"
				userAssignment={userAssignment}
				campaignId={campaignId}
				blockId={blockId}
				displayCreators={[]}
				clientVisible={clientVisible}
				viewMode="grid"
				creatorVisibility={creatorVisibility}
				isCustomizing={isCustomizing}
				onAddCreator={handleAddCreator}
				onGetUrl={handleGetUrl}
				onCustomizeOrder={handleCustomizeOrder}
				onSaveOrder={handleSaveOrder}
				isSaving={isSaving}
				canManage={canManage}
				onClientVisibleToggle={handleClientVisibleToggle}
				onCreatorVisibilityToggle={handleCreatorVisibilityToggle}
				onSettingsClick={handleSettingsClick}
				onCommentClick={handleCommentClick}
				onCustomizeDataInGroups={handleCustomizeDataInGroups}
				onCustomizeOrderButton={handleCustomizeOrder}
				onOrderChange={() => {}}
				renderGroupingContent={() => (
					<EmptyGroups>
						No creator groups yet. {!isClientOnly && 'Click "Create New Group" to add one'}
					</EmptyGroups>
				)}
			/>
		);
	}

	return (
		<>
			<CreatorDisplayLayout
				pageTitle="Creator Grouping"
				addCreatorText="Create New Group"
				displayId=""
				mode="grouping"
				userAssignment={userAssignment}
				campaignId={campaignId}
				blockId={blockId}
				displayCreators={[]}
				clientVisible={clientVisible}
				viewMode="grid"
				creatorVisibility={creatorVisibility}
				isCustomizing={isCustomizing}
				onAddCreator={handleAddCreator}
				onGetUrl={handleGetUrl}
				onCustomizeOrder={handleCustomizeOrder}
				onSaveOrder={handleSaveOrder}
				isSaving={isSaving}
				canManage={canManage}
				onClientVisibleToggle={handleClientVisibleToggle}
				onCreatorVisibilityToggle={handleCreatorVisibilityToggle}
				onSettingsClick={handleSettingsClick}
				onCommentClick={handleCommentClick}
				onCustomizeDataInGroups={handleCustomizeDataInGroups}
				onCustomizeOrderButton={handleCustomizeOrder}
				onOrderChange={() => {}}
				onCategoryTierChange={handleCategoryTierChange}
				renderGroupingContent={() => (
					<CreatorGroupGrid
						groups={displayGroupsWithSummary}
						isCustomizing={isCustomizing}
						creatorVisibility={creatorVisibility}
						isSaving={isSaving}
						onCreatorVisibilityToggle={handleCreatorVisibilityToggle}
						onGroupReorder={handleGroupReorder}
						onCommentClick={handleCommentClick}
						onSettingsClick={handleSettingsClick}
						onDeleteCreator={handleDeleteCreator}
						onDeleteGroup={handleDeleteGroup}
						onSeeApprovals={handleSeeApprovals}
						onGroupVisibilityToggle={handleGroupVisibilityToggle}
						onEditGroup={handleEditGroup}
						onAddToCampaign={handleAddToCampaign}
						onRemoveFromCampaign={handleRemoveFromCampaign}
						isClientOnly={isClientOnly}
						canManage={canManage}
						campaignId={campaignId || undefined}
						blockId={blockId || undefined}
						finalRateCurrency={finalRateCurrency}
						finalRateConvertedCurrency={finalRateConvertedCurrency}
						onCategoryTierChange={handleCategoryTierChange}
						contentExecutionBlockId={contentExecutionBlockId}
						campaignName={campaignName}
						onContactEmailChange={updateCreatorContactEmail}
						onBatchAdded={(assignmentIds) => {
							assignmentIds.forEach((assignmentId) => updateCreatorCampaignStatus(assignmentId, true));
							onCreatorCampaignStatusChanged?.();
						}}
					/>
				)}
			/>
			<DynamicModal
				open={Boolean(creatorToDelete)}
				onCancel={() => setCreatorToDelete(null)}
				title="Delete Creator from Group"
				description={
					creatorToDelete
						? `Are you sure you want to delete ${creatorToDelete.creatorName} from the group?`
						: "Are you sure you want to delete this creator from the group?"
				}
				variant="danger"
				icon={{ src: "/icon/trash.svg", color: "#D30024", size: 24 }}
				confirmText="Delete"
				onConfirm={handleConfirmDeleteCreator}
				loading={isDeletingCreator}
			/>
			<DynamicModal
				open={Boolean(creatorToRemoveFromCampaign)}
				onCancel={() => setCreatorToRemoveFromCampaign(null)}
				title="Remove from campaign"
				description={
					creatorToRemoveFromCampaign
						? `Are you sure you want to remove ${creatorToRemoveFromCampaign.creatorName} from the campaign?`
						: "Are you sure you want to remove this creator from the campaign?"
				}
				variant="danger"
				icon={{ src: "/icon/trash.svg", color: "#D30024", size: 24 }}
				confirmText="Remove"
				onConfirm={handleConfirmRemoveFromCampaign}
				loading={isRemovingFromCampaign}
			/>
			<AddCreatorModal
				visible={addCreatorModalVisible}
				creator={selectedCreator}
				submitting={isSubmitting}
				onCancel={handleCancelAddToCampaign}
				onSubmit={handleSubmitAddToCampaign}
			/>
			<DynamicModal
				open={engagementTermsRequiredModalVisible}
				onCancel={handleCancelEngagementTermsRequired}
				title="Engagement Term required"
				description="Please create an Engagement Term first before adding this creator to Content Execution."
				icon={{ src: "/icon/info.svg", color: "#3A0CA3", size: 24 }}
				confirmText="Create Engagement Term"
				cancelText="Cancel"
				onConfirm={handleGoToEngagementTerms}
			/>
		</>
	);
};

const getPlatformUsername = (creator: Creator): string => {
	if (creator.tiktok?.platformUsername) return creator.tiktok.platformUsername;
	if (creator.instagram?.platformUsername) return creator.instagram.platformUsername;
	if (creator.facebook?.platformUsername) return creator.facebook.platformUsername;
	if (creator.youtube?.platformUsername) return creator.youtube.platformUsername;
	return creator.name || "Creator";
};

// Styled components for Add Creator Modal
const CreatorInfoSection = styled.div`
	display: flex;
	align-items: center;
	gap: 16px;
	min-height: 88px;
	margin-bottom: 20px;
	padding: 16px;
	border-radius: 12px;
	background: #fff;

	${mediaPhone`
		flex-wrap: wrap;
		gap: 12px;
		min-height: 0;
		padding: 12px;
	`}
`;

const ProfileImage = styled(ResponsiveAvatar)`
	width: 56px;
	height: 56px;
	border-radius: 50%;
	object-fit: cover;
	flex-shrink: 0;
`;

const CreatorDetails = styled.div`
	flex: 1;
	min-width: 0;
`;

const CreatorName = styled.div`
	font-size: 18px;
	font-weight: 600;
	color: #303030;
	margin-bottom: 4px;
	overflow-wrap: anywhere;

	${mediaPhone`
		font-size: 16px;
	`}
`;

const CreatorTier = styled.div`
	font-size: 14px;
	color: #707070;
`;

const SocialIcons = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	margin-left: auto;

	${mediaPhone`
		flex: 1 1 100%;
		margin-left: 68px;
	`}
`;

const SocialIcon = styled(Icon)`
	width: 32px;
	height: 32px;
	border-radius: 8px;
	object-fit: contain;
`;

const CreatorEmailField = styled.label`
	display: grid;
	gap: 6px;
	margin: 0;
	color: #707070;
	font-size: 14px;

	.ant-input {
		height: 41px;
		border-color: #cdd4f6;
		border-radius: 8px;
		font-size: 14px;
	}
`;

interface AddCreatorModalProps {
	visible: boolean;
	creator?: Creator;
	submitting: boolean;
	onCancel: () => void;
	onSubmit: (email: string | null) => void;
}

const AddCreatorModal: React.FC<AddCreatorModalProps> = ({ visible, creator, submitting, onCancel, onSubmit }) => {
	const [email, setEmail] = useState("");
	const [emailError, setEmailError] = useState("");

	useEffect(() => {
		if (visible) {
			setEmail(creator?.contactEmail || creator?.email || "");
			setEmailError("");
		}
	}, [creator, visible]);

	if (!creator) return null;
	const submit = () => {
		const normalized = normalizeEmail(email);
		if (normalized && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized)) {
			setEmailError("Enter a valid email address");
			return;
		}
		setEmailError("");
		onSubmit(normalized || null);
	};

	const hasTiktok = creator.tiktok || creator.TikTok;
	const hasInstagram = creator.instagram || creator.Instagram;
	const hasFacebook = creator.facebook || creator.Facebook;
	const hasYoutube = creator.youtube || creator.YouTube;

	return (
		<DynamicModal
			open={visible}
			onCancel={onCancel}
			title="Add Creator in the Campaign"
			width={520}
			confirmText={submitting ? "Adding..." : "Add"}
			onConfirm={submit}
			loading={submitting}
			fullWidthActions
		>
			<CreatorInfoSection>
				<ProfileImage
					displaySize={56}
					src={creator.img_url || "/default_avatar.png"}
					alt={creator.name}
					onError={(e) => {
						const t = e.currentTarget;
						if (t.src.endsWith("/default_avatar.png")) return;
						t.src = "/default_avatar.png";
					}}
				/>
				<CreatorDetails>
					<CreatorName>{creator.name || "Creator"}</CreatorName>
					<CreatorTier>{creator.userId ? "Returning creator" : "Creator"}</CreatorTier>
				</CreatorDetails>
				<SocialIcons>
					{hasTiktok && <SocialIcon name="tiktok" alt="TikTok" preserveOriginalColor />}
					{hasFacebook && <SocialIcon name="facebook" alt="Facebook" preserveOriginalColor />}
					{hasYoutube && <SocialIcon name="youtube" alt="YouTube" preserveOriginalColor />}
					{hasInstagram && <SocialIcon name="instagram" alt="Instagram" preserveOriginalColor />}
				</SocialIcons>
			</CreatorInfoSection>
			<CreatorEmailField>
				<span>Email Address</span>
				<Input
					type="email"
					value={email}
					status={emailError ? "error" : undefined}
					placeholder="creator@email.com"
					disabled={submitting || Boolean(creator.userId)}
					onChange={(event) => {
						setEmail(event.target.value);
						if (emailError) setEmailError("");
					}}
					onPressEnter={submit}
				/>
				{emailError && <span style={{ color: "#f72585", fontSize: 12 }}>{emailError}</span>}
			</CreatorEmailField>
		</DynamicModal>
	);
};
